<template>
  <div class="chart-container" :style="{ height: height }">
    <div v-if="loading" class="chart-loading">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <div v-else-if="error" class="chart-error">
      <i class="fas fa-exclamation-circle"></i>
    </div>
    <v-chart v-else class="chart" :option="chartOption" autoresize />
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import api from '../api/config';
import { getStageColor, hexToRgba } from '../utils/stageColors';

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkPointComponent,
]);

export default {
  name: 'AudienceChart',
  components: {
    VChart,
  },
  props: {
    artist: {
      type: String,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
    stage: {
      type: String,
      required: true,
    },
    height: {
      type: String,
      default: '200px',
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      chartData: [],
    };
  },
  computed: {
    stageColor() {
      return getStageColor(this.stage);
    },
    chartOption() {
      // Ensure chartData is valid and has items
      if (!this.chartData || !Array.isArray(this.chartData) || this.chartData.length === 0) {
        return {
          animation: false,
          grid: {
            top: 30,
            right: 10,
            bottom: 30,
            left: 10,
          },
          xAxis: {
            type: 'category',
            data: [],
            show: false,
          },
          yAxis: {
            type: 'value',
            show: false,
          },
          series: [{
            type: 'line',
            data: [],
          }]
        };
      }

      // Find peak value and its index
      const data = this.chartData.map(item => Number(item.count) || 0);
      const peakValue = Math.max(...data);
      const peakIndex = data.indexOf(peakValue);
      
      return {
        animation: false,
        grid: {
          top: 30,
          right: 10,
          bottom: 30,
          left: 10,
        },
        tooltip: {
          show: true,
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderColor: this.stageColor,
          borderWidth: 1,
          textStyle: {
            color: '#666',
            fontSize: 12,
          },
          formatter: (params) => {
            if (!params || !params[0]) return '';
            const data = params[0];
            return `${data.name}<br/>观众数：${data.value}`;
          },
          position: function (pos, params, el, elRect, size) {
            const obj = { top: 10 };
            obj[['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]] = 30;
            return obj;
          }
        },
        xAxis: {
          type: 'category',
          data: this.chartData.map(item => {
            if (item && item.time && typeof item.time === 'string') {
              const parts = item.time.split(' ');
              return parts.length > 1 ? parts[1] : item.time;
            }
            return '';
          }),
          show: false,
        },
        yAxis: {
          type: 'value',
          show: false,
        },
        series: [
          {
            type: 'line',
            data: data,
            smooth: true,
            symbol: 'none',
            lineStyle: {
              width: 1,
              color: this.stageColor,
            },
            markPoint: {
              symbol: 'circle',
              symbolSize: 6,
              itemStyle: {
                color: this.stageColor,
              },
              label: {
                show: true,
                formatter: '{c}',
                position: 'top',
                distance: 5,
                fontSize: 12,
                color: '#666',
              },
              data: data.length > 0 ? [
                {
                  name: '开始',
                  coord: [0, data[0]],
                  label: {
                    formatter: `开始: ${data[0]}`,
                  }
                },
                {
                  name: '峰值',
                  coord: [peakIndex, peakValue],
                  label: {
                    formatter: `峰值: ${peakValue}`,
                  }
                },
                {
                  name: '结束',
                  coord: [data.length - 1, data[data.length - 1]],
                  label: {
                    formatter: `结束: ${data[data.length - 1]}`,
                  }
                }
              ] : []
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: hexToRgba(this.stageColor, 0.2) },
                  { offset: 1, color: hexToRgba(this.stageColor, 0.05) },
                ],
              },
            },
          },
        ],
      };
    },
  },
  watch: {
    artist: 'fetchData',
    date: 'fetchData',
    stage: 'fetchData',
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      if (!this.artist || !this.date || !this.stage) {
        this.error = '缺少必要参数';
        this.loading = false;
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        
        const response = await api.get(`/artist-performance/${encodeURIComponent(this.artist)}`);
        
        if (!response || !response.data || !response.data.data) {
          throw new Error('Invalid response format');
        }

        // Process the data to ensure it has the expected format
        this.chartData = response.data.data
          .filter(item => item && typeof item === 'object')
          .map(item => ({
            time: item.rounded_datetime || '',
            count: Number(item.total_count) || 0
          }))
          .filter(item => item.time && !isNaN(item.count));
        
        if (this.chartData.length === 0) {
          this.error = '没有可用的数据';
        }
        
        this.loading = false;
      } catch (error) {
        console.error('Error fetching chart data:', error);
        this.error = '加载图表数据失败';
        this.loading = false;
        this.chartData = [];
      }
    },
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.chart {
  width: 100%;
  height: 100%;
}

.chart-loading,
.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(0, 0, 0, 0.2);
}
</style> 